import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import i18n, { loadLocale } from '../i18n';
import {
   resolveLanguage,
   setUserOverride,
} from '../i18n/resolveLanguage';
import { DEFAULT_LANGUAGE } from '../i18n/languages';
import { resolveCurrency, setCurrencyOverride } from '../i18n/resolveCurrency';

const AppContext = createContext();

// Common Provider component
export const CommonProvider = ({ children }) => {
   // SnackConfig state
   const [snackConfig, setSnackConfigState] = useState({
      open: false,
      message: "",
      severity: "success",
      timeout: 4000,
      bgColor: 'var(--brand-color)',
   });

   const setSnackConfig = (config) => {
      setSnackConfigState((prev) => ({ ...prev, ...config }));
   };

   // Language state ---------------------------------------------------
   const [language, setLanguage] = useState(() =>
      resolveLanguage({ userPreferred: null, creatorDefault: null }) || DEFAULT_LANGUAGE
   );
   const [creatorDefaultLanguage, setCreatorDefaultLanguage] = useState(null);
   const [userPreferredLanguage, setUserPreferredLanguage] = useState(null);

   // Re-resolve whenever creator/user inputs change (but never override an
   // explicit user choice — resolveLanguage checks localStorage first).
   useEffect(() => {
      const resolved = resolveLanguage({
         userPreferred: userPreferredLanguage,
         creatorDefault: creatorDefaultLanguage,
      });
      if (resolved && resolved !== language) {
         setLanguage(resolved);
      }
   }, [creatorDefaultLanguage, userPreferredLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

   // Keep i18next + <html lang> in sync with state.
   useEffect(() => {
      if (!language) return;
      if (i18n.language !== language) {
         loadLocale(language).then(() => i18n.changeLanguage(language));
      }
      if (typeof document !== 'undefined') {
         document.documentElement.lang = language;
      }
   }, [language]);

   // Explicit user action: persist, flip UI, fire-and-forget sync to server.
   const changeLanguage = useCallback(async (code) => {
      const normalized = setUserOverride(code);
      if (!normalized) return;
      await loadLocale(normalized);
      await i18n.changeLanguage(normalized);
      setLanguage(normalized);
      // Non-blocking server sync; localStorage is the source of truth.
      try {
         const formData = new FormData();
         formData.append('preferred_language', normalized);
         axios
            .post('/api/user/update_preferred_language', formData, { withCredentials: true })
            .catch(() => {});
      } catch (e) {
         // swallow — not fatal
      }
   }, []);

   // Currency state ---------------------------------------------------
   const [currency, setCurrency] = useState(() => resolveCurrency());

   const changeCurrency = useCallback((code) => {
      const normalized = setCurrencyOverride(code);
      if (!normalized) return;
      setCurrency(normalized);
      // Dispatch event so class components can listen for live updates
      window.dispatchEvent(new CustomEvent('withme-currency-change', { detail: normalized }));
      // Reload so all API calls re-fetch prices for the new currency
      window.location.reload();
   }, []);

   const value = {
      snackConfig,
      setSnackConfig,
      // language
      language,
      creatorDefaultLanguage,
      setCreatorDefaultLanguage,
      userPreferredLanguage,
      setUserPreferredLanguage,
      changeLanguage,
      // currency
      currency,
      changeCurrency,
   };

   return (
      <AppContext.Provider value={value}>
         {children}
      </AppContext.Provider>
   );
};

// Custom hook to use the context (for functional components)
export const useApp = () => {
   const context = useContext(AppContext);
   if (!context) {
      throw new Error('useApp must be used within a AppProvider');
   }
   return context;
};

export default AppContext;
